// src/hooks/useAIOpponent.ts
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Position, Piece } from "../types/Board";
import { AIEngine, AISettings as AISettingsType } from "../engine/AIEngine.ts";
import { AISettingsState } from "../components/AISettings";
import { getPieceAt, createBoardObject, boardToFen } from "../utils/gameHelpers.ts";
import { MoveAIContext } from "../utils/MoveHistoryManager.ts";
import { isWasmAvailable } from "../engine/WasmChessEngine.ts";
import {
  getNativeEngineClient,
  isNativeEngineAvailable,
  initNativeEngine,
  EvaluationWeights,
  TertiarySettings,
  PositionContextData,
} from "../engine/NativeEngineClient.ts";
import { PIECE_DEFINITIONS } from "../data/pieceDefinitions.ts";
// Note: Difficulty ramping removed - user's actual settings from AI Settings page are now used directly

// Engine type for display - native = Rust 20-thread parallel search
export type AIEngineType = "native" | "typescript" | "wasm" | "none";

export interface AIMove {
  from: Position;
  to: Position;
  capturedPiece?: Piece;
}

interface UseAIOpponentProps {
  aiSettings: AISettingsState | null;
  vsComputer: boolean;
  aiColor: "white" | "black";
  currentTurn: "white" | "black";
  gameStatus: "active" | "check" | "checkmate" | "stalemate";
  isReviewingHistory: boolean;
  moveHistory: any[];
  pieces: Map<string, Piece>;
  boardRows: number;
  boardCols: number;
  currentGameId: string;
  onAIMove: (from: Position, to: Position, capturedPiece?: Piece, aiContext?: MoveAIContext) => void;
}

export const useAIOpponent = ({
  aiSettings,
  vsComputer,
  aiColor,
  currentTurn,
  gameStatus,
  isReviewingHistory,
  moveHistory,
  pieces,
  boardRows,
  boardCols,
  currentGameId,
  onAIMove,
}: UseAIOpponentProps) => {
  const [aiEngine, setAiEngine] = useState<AIEngine | null>(null);
  const [isAiThinking, setIsAiThinking] = useState<boolean>(false);
  const [aiThinkingTime, setAiThinkingTime] = useState<number>(0);
  const [aiStats, setAiStats] = useState<{
    thinkingTime: number;
    nodesEvaluated: number;
    depth: number;
    nps?: number;          // Nodes per second (native engine)
    threadsUsed?: number;  // Threads used (native engine)
    lastMove?: string;     // The actual move made (e.g., "K e1-g1")
    // Position-Aware Heuristic System context (Phase 7)
    positionContext?: PositionContextData;
  } | null>(null);

  // Helper to convert position to algebraic notation
  const positionToAlgebraic = (row: number, col: number, totalRows: number): string => {
    const file = String.fromCharCode('a'.charCodeAt(0) + col);
    const rank = totalRows - row;
    return `${file}${rank}`;
  };

  // Helper to get piece abbreviation (matches move history panel format)
  const getPieceAbbreviation = (piece: Piece | null): string => {
    if (!piece) return "?";
    // First check if piece has abbreviation directly (like move history panel does)
    if (piece.abbreviation) {
      return piece.abbreviation.toUpperCase();
    }
    // Fall back to PIECE_DEFINITIONS lookup
    const pieceType = piece.type?.toLowerCase();
    if (pieceType) {
      const pieceDefinition = PIECE_DEFINITIONS[pieceType];
      if (pieceDefinition?.abbreviation) {
        return pieceDefinition.abbreviation.toUpperCase();
      }
    }
    return piece.type?.substring(0, 3).toUpperCase() || "?";
  };

  // Track which engine type is active
  const [engineType, setEngineType] = useState<AIEngineType>("none");

  // Native engine availability
  const [nativeAvailable, setNativeAvailable] = useState<boolean>(false);
  const nativeCheckedRef = useRef(false);

  // ============================================
  // REFS - Store values that shouldn't trigger re-renders
  // ============================================
  const aiEngineRef = useRef<AIEngine | null>(null);
  const onAIMoveRef = useRef(onAIMove);
  const piecesRef = useRef(pieces);
  const boardRowsRef = useRef(boardRows);
  const boardColsRef = useRef(boardCols);
  const currentGameIdRef = useRef(currentGameId);
  const aiColorRef = useRef(aiColor);
  const threadCountRef = useRef(aiSettings?.threadCount ?? 20);
  const maxThinkingTimeRef = useRef(aiSettings?.maxThinkingTime ?? 10);
  const difficultyRef = useRef(aiSettings?.difficulty ?? 'medium');
  const engineTypeRef = useRef(aiSettings?.engineType ?? 'auto');
  const priorityOrderRef = useRef(aiSettings?.priorityOrder ?? [
    'checkmate', 'capture_high', 'check', 'exchange_favorable', 'fork', 'threat', 'development', 'castling'
  ]);

  // NEW: Evaluation weights refs for passing to Rust engine
  const materialBalanceRef = useRef(aiSettings?.materialBalance ?? 1.0);
  const piecePositioningRef = useRef(aiSettings?.piecePositioning ?? 1.0);
  const kingSafetyRef = useRef(aiSettings?.kingSafety ?? 1.0);
  const mobilityRef = useRef(aiSettings?.mobility ?? 0.0);
  const pawnStructureRef = useRef(aiSettings?.pawnStructure ?? true);
  const boardControlRef = useRef(aiSettings?.boardControl ?? false);

  // NEW: Tertiary settings refs for passing to Rust engine
  const depthCapRef = useRef(aiSettings?.depthCap ?? 8);
  const quiescenceDepthRef = useRef(aiSettings?.quiescenceDepth ?? 26);
  const aspirationWindowRef = useRef(aiSettings?.aspirationWindow ?? 60);
  const lmrThresholdRef = useRef(aiSettings?.lmrThreshold ?? 4);
  const nullMoveRRef = useRef(aiSettings?.nullMoveR ?? 2);
  const ttSizeMbRef = useRef(aiSettings?.ttSizeMb ?? 256);

  // CRITICAL: Lock to prevent re-entry and duplicate AI moves
  const aiMoveLockRef = useRef(false);
  const lastMoveCountRef = useRef(0);

  // Track move history length for difficulty ramping
  const moveHistoryLengthRef = useRef(0);

  // Keep refs updated (silently, no re-renders)
  onAIMoveRef.current = onAIMove;
  piecesRef.current = pieces;
  boardRowsRef.current = boardRows;
  boardColsRef.current = boardCols;
  currentGameIdRef.current = currentGameId;
  aiColorRef.current = aiColor;
  threadCountRef.current = aiSettings?.threadCount ?? 20;
  maxThinkingTimeRef.current = aiSettings?.maxThinkingTime ?? 10;
  difficultyRef.current = aiSettings?.difficulty ?? 'medium';
  engineTypeRef.current = aiSettings?.engineType ?? 'auto';
  priorityOrderRef.current = aiSettings?.priorityOrder ?? [
    'checkmate', 'capture_high', 'check', 'exchange_favorable', 'fork', 'threat', 'development', 'castling'
  ];
  // Update evaluation weight refs
  materialBalanceRef.current = aiSettings?.materialBalance ?? 1.0;
  piecePositioningRef.current = aiSettings?.piecePositioning ?? 1.0;
  kingSafetyRef.current = aiSettings?.kingSafety ?? 1.0;
  mobilityRef.current = aiSettings?.mobility ?? 0.0;
  pawnStructureRef.current = aiSettings?.pawnStructure ?? true;
  boardControlRef.current = aiSettings?.boardControl ?? false;
  // Update tertiary settings refs
  depthCapRef.current = aiSettings?.depthCap ?? 8;
  quiescenceDepthRef.current = aiSettings?.quiescenceDepth ?? 26;
  aspirationWindowRef.current = aiSettings?.aspirationWindow ?? 60;
  lmrThresholdRef.current = aiSettings?.lmrThreshold ?? 4;
  nullMoveRRef.current = aiSettings?.nullMoveR ?? 2;
  ttSizeMbRef.current = aiSettings?.ttSizeMb ?? 256;
  // Update move history length ref
  moveHistoryLengthRef.current = moveHistory.length;

  // Get search depth based on difficulty level
  const getSearchDepthForDifficulty = (difficulty: string): number => {
    const depths: Record<string, number> = {
      easy: 3,
      medium: 6,
      hard: 9,
      expert: 12,
      ultra: 15
    };
    return depths[difficulty] ?? 6;
  };

  // Create a stable string key for settings comparison
  const aiSettingsKey = useMemo(() => {
    if (!aiSettings) return 'null';
    return [
      aiSettings.difficulty,
      aiSettings.materialBalance.toFixed(4),
      aiSettings.piecePositioning.toFixed(4),
      aiSettings.kingSafety.toFixed(4),
      aiSettings.mobility.toFixed(4),
      aiSettings.pawnStructure,
      aiSettings.boardControl,
      aiSettings.priorityOrder.join('-'),
      aiSettings.maxThinkingTime,
      aiSettings.useIterativeDeepening,
      aiSettings.useAlphaBetaPruning,
    ].join('|');
  }, [
    aiSettings?.difficulty,
    aiSettings?.materialBalance,
    aiSettings?.piecePositioning,
    aiSettings?.kingSafety,
    aiSettings?.mobility,
    aiSettings?.pawnStructure,
    aiSettings?.boardControl,
    aiSettings?.priorityOrder,
    aiSettings?.maxThinkingTime,
    aiSettings?.useIterativeDeepening,
    aiSettings?.useAlphaBetaPruning,
  ]);

  // Check native engine availability on mount
  useEffect(() => {
    if (!nativeCheckedRef.current) {
      nativeCheckedRef.current = true;
      initNativeEngine().then((available) => {
        setNativeAvailable(available);
        if (available) {
          console.log("🚀 Native 20-thread Rust engine available!");
        }
      });
    }
  }, []);

  // Initialize AI Engine only when settings key changes
  useEffect(() => {
    if (aiSettings && vsComputer) {
      const engineSettings: AISettingsType = {
        difficulty: aiSettings.difficulty,
        weights: {
          materialBalance: aiSettings.materialBalance,
          piecePositioning: aiSettings.piecePositioning,
          kingSafety: aiSettings.kingSafety,
          mobility: aiSettings.mobility,
          pawnStructure: aiSettings.pawnStructure,
          boardControl: aiSettings.boardControl,
        },
        priorityOrder: aiSettings.priorityOrder,
        maxThinkingTime: aiSettings.maxThinkingTime,
        useIterativeDeepening: aiSettings.useIterativeDeepening,
        useAlphaBetaPruning: aiSettings.useAlphaBetaPruning,
      };

      // Create TypeScript engine as fallback
      const engine = new AIEngine(engineSettings);
      setAiEngine(engine);
      aiEngineRef.current = engine;

      // Determine engine type based on user preference
      let currentEngineType: AIEngineType;
      const userEnginePreference = aiSettings.engineType ?? 'auto';

      if (userEnginePreference === 'rust') {
        // User explicitly selected Rust engine
        currentEngineType = nativeAvailable ? "native" : "typescript";
        if (!nativeAvailable) {
          console.warn("🦀 Rust engine requested but not available, falling back to TypeScript");
        }
      } else if (userEnginePreference === 'typescript') {
        // User explicitly selected TypeScript engine
        currentEngineType = "typescript";
      } else {
        // Auto mode: native > wasm > typescript
        if (nativeAvailable) {
          currentEngineType = "native";
        } else if (isWasmAvailable()) {
          currentEngineType = "wasm";
        } else {
          currentEngineType = "typescript";
        }
      }
      setEngineType(currentEngineType);

      console.log("🤖 AI Engine initialized:", {
        difficulty: aiSettings.difficulty,
        userPreference: userEnginePreference,
        engineType: currentEngineType,
        nativeAvailable,
        wasmAvailable: isWasmAvailable(),
      });
    } else {
      setAiEngine(null);
      aiEngineRef.current = null;
      setEngineType("none");
    }
  }, [aiSettingsKey, vsComputer, nativeAvailable]);

  // Thinking time counter
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isAiThinking) {
      const startTime = Date.now();
      setAiThinkingTime(0);
      interval = setInterval(() => {
        setAiThinkingTime((Date.now() - startTime) / 1000);
      }, 100);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAiThinking]);

  /**
   * Execute AI move using native engine (20-thread parallel search) with TypeScript fallback
   */
  const executeAIMove = useCallback(async (currentPieces: Map<string, Piece>) => {
    // Multiple guards against re-entry
    if (aiMoveLockRef.current) {
      console.log("🤖 AI move blocked - lock active");
      return;
    }

    const engine = aiEngineRef.current;
    if (!engine && !nativeAvailable) {
      console.log("🤖 AI move blocked - no engine available");
      return;
    }

    // Acquire lock
    aiMoveLockRef.current = true;
    setIsAiThinking(true);
    console.log("🤖 AI is thinking...");
    console.log("🤖 Board has", currentPieces.size, "pieces");

    // Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const board = createBoardObject(
        currentPieces,
        boardRowsRef.current,
        boardColsRef.current,
        currentGameIdRef.current
      );

      // Determine which engine to use based on user preference
      const userEngineChoice = engineTypeRef.current;
      const shouldUseRust = userEngineChoice === 'rust' ||
        (userEngineChoice === 'auto' && nativeAvailable);
      const forceTypeScript = userEngineChoice === 'typescript';

      // Try native engine if appropriate (parallel search with configurable threads)
      if (shouldUseRust && nativeAvailable && !forceTypeScript) {
        // Use user's actual settings from refs (these are updated when Apply Changes is clicked)
        const threads = threadCountRef.current;
        const timeMs = maxThinkingTimeRef.current * 1000; // Convert seconds to ms
        const maxDepth = depthCapRef.current;
        const priorityOrder = priorityOrderRef.current;

        // Build evaluation weights from user's actual settings
        const weights: EvaluationWeights = {
          material: materialBalanceRef.current,
          position: piecePositioningRef.current,
          kingSafety: kingSafetyRef.current,
          mobility: mobilityRef.current,
          pawnStructure: pawnStructureRef.current,
          boardControl: boardControlRef.current,
        };

        // Build tertiary settings from user's actual settings
        const tertiary: TertiarySettings = {
          quiescenceDepth: quiescenceDepthRef.current,
          aspirationWindow: aspirationWindowRef.current,
          lmrThreshold: lmrThresholdRef.current,
          nullMoveR: nullMoveRRef.current,
          ttSizeMb: ttSizeMbRef.current,
        };

        // Log for debugging - show user's actual settings being used
        const historyLength = moveHistoryLengthRef.current;
        const aiMoveNumber = aiColorRef.current === 'black'
          ? Math.floor(historyLength / 2) + 1
          : Math.ceil(historyLength / 2) + 1;
        console.log(`🚀 AI move ${aiMoveNumber}, using USER settings (not presets)`);
        console.log(`🚀 Using NATIVE Rust engine (user: ${userEngineChoice}) with ${threads} threads, ${timeMs}ms time limit, depth ${maxDepth}`);
        console.log(`🚀 Tertiary: qd=${tertiary.quiescenceDepth}, asp=${tertiary.aspirationWindow}, lmr=${tertiary.lmrThreshold}, nmr=${tertiary.nullMoveR}`);
        console.log(`🚀 Priority order: ${priorityOrder.join(', ')}`);
        console.log(`🚀 Evaluation weights: material=${weights.material.toFixed(2)}, position=${weights.position.toFixed(2)}, kingSafety=${weights.kingSafety.toFixed(2)}, mobility=${weights.mobility.toFixed(2)}`);

        // Calculate full game move number for FEN (used by engine for phase detection)
        // Full move number = floor(ply / 2) + 1 (1-indexed, increments after Black moves)
        const gameMoveNumber = Math.floor(historyLength / 2) + 1;
        const fen = boardToFen(board, aiColorRef.current, gameMoveNumber);
        console.log("🤖 FEN:", fen, `(move ${gameMoveNumber})`);

        const nativeClient = getNativeEngineClient();
        const result = await nativeClient.search(fen, {
          depth: maxDepth,
          time_ms: timeMs,
          threads: threads,
          priority_order: priorityOrder,
          weights: weights,
          tertiary: tertiary,
        });

        if (result.success && result.from && result.to) {
          console.log(
            `🚀 Native engine move: ${result.from.row},${result.from.col} -> ${result.to.row},${result.to.col}`
          );
          console.log(
            `🚀 Stats: depth=${result.depth}, nodes=${result.nodes}, nps=${result.nps}, threads=${result.threads_used}`
          );

          const movingPiece = getPieceAt(result.from.row, result.from.col, currentPieces);
          const pieceAbbrev = getPieceAbbreviation(movingPiece);
          const moveFrom = positionToAlgebraic(result.from.row, result.from.col, boardRowsRef.current);
          const moveTo = positionToAlgebraic(result.to.row, result.to.col, boardRowsRef.current);

          setAiStats({
            thinkingTime: result.time_ms / 1000,
            nodesEvaluated: result.nodes,
            depth: result.depth,
            nps: result.nps,
            threadsUsed: result.threads_used,
            lastMove: `${pieceAbbrev} ${moveFrom}-${moveTo}`,
            positionContext: result.position_context,
          });

          // Get piece at to position to check for captured piece
          const capturedPiece = getPieceAt(result.to.row, result.to.col, currentPieces);

          // Convert position context to MoveAIContext for move history
          const aiContext: MoveAIContext | undefined = result.position_context ? {
            game_phase: result.position_context.game_phase,
            situation: result.position_context.situation,
            is_emergency: result.position_context.is_emergency,
            primary_goal: result.position_context.primary_goal,
            goals: result.position_context.goals,
            profile: result.position_context.profile,
          } : undefined;

          // Execute move via callback (with AI context for heuristics verification)
          onAIMoveRef.current(result.from, result.to, capturedPiece || undefined, aiContext);
          return;
        } else {
          console.warn("🚀 Native engine failed, falling back to TypeScript:", result.error);
        }
      }

      // Fallback to TypeScript engine
      if (engine) {
        console.log("🤖 Using TypeScript engine (fallback)");
        console.log("🤖 Board object created, calling getBestMove for", aiColorRef.current);

        const aiMove = await engine.getBestMove(board, aiColorRef.current);

        console.log("🤖 getBestMove returned:", aiMove);

        if (aiMove) {
          console.log(
            `🤖 AI selected move: ${aiMove.from.row},${aiMove.from.col} -> ${aiMove.to.row},${aiMove.to.col}`
          );

          const tsMovingPiece = getPieceAt(aiMove.from.row, aiMove.from.col, currentPieces);
          const tsPieceAbbrev = getPieceAbbreviation(tsMovingPiece);
          const tsMoveFrom = positionToAlgebraic(aiMove.from.row, aiMove.from.col, boardRowsRef.current);
          const tsMoveTo = positionToAlgebraic(aiMove.to.row, aiMove.to.col, boardRowsRef.current);

          setAiStats({
            thinkingTime: aiMove.thinkingTime,
            nodesEvaluated: aiMove.nodesEvaluated,
            depth: aiMove.depth,
            lastMove: `${tsPieceAbbrev} ${tsMoveFrom}-${tsMoveTo}`,
          });

          const piece = tsMovingPiece;
          if (piece) {
            // Execute move via callback (no AI context for TypeScript fallback)
            onAIMoveRef.current(aiMove.from, aiMove.to, aiMove.capturedPiece, undefined);
          } else {
            console.error("❌ AI tried to move non-existent piece");
          }
        } else {
          console.error("❌ AI returned no move");
        }
      }
    } catch (error) {
      console.error("❌ AI move error:", error);
    } finally {
      setIsAiThinking(false);
      // Release lock after a delay to prevent immediate re-trigger
      setTimeout(() => {
        aiMoveLockRef.current = false;
      }, 100);
    }
  }, [nativeAvailable]);

  // Trigger AI move - ONLY based on turn change
  useEffect(() => {
    // Skip if not playing against AI
    if (!vsComputer) {
      return;
    }
    
    // Skip if no engine
    if (!aiEngineRef.current) {
      return;
    }
    
    // Skip if not AI's turn
    if (currentTurn !== aiColor) {
      return;
    }
    
    // Skip if game is over
    if (gameStatus !== "active" && gameStatus !== "check") {
      return;
    }
    
    // Skip if reviewing history
    if (isReviewingHistory) {
      return;
    }
    
    // Skip if no moves yet (player hasn't moved)
    if (moveHistory.length === 0) {
      return;
    }
    
    // Skip if lock is active (AI is already processing)
    if (aiMoveLockRef.current) {
      return;
    }
    
    console.log("🤖 ✅ All checks passed! Scheduling AI move with", pieces.size, "pieces");
    
    // Capture current pieces at trigger time
    const currentPieces = pieces;
    
    const timer = setTimeout(() => {
      executeAIMove(currentPieces);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [
    vsComputer,
    currentTurn,
    aiColor,
    gameStatus,
    isReviewingHistory,
    moveHistory.length,
    pieces,
    executeAIMove,
  ]);

  // Reset when game resets
  useEffect(() => {
    if (moveHistory.length === 0) {
      lastMoveCountRef.current = 0;
      aiMoveLockRef.current = false;
    }
  }, [moveHistory.length]);

  const resetAIStats = useCallback(() => {
    setAiStats(null);
    setIsAiThinking(false);
    setAiThinkingTime(0);
    aiMoveLockRef.current = false;
    lastMoveCountRef.current = 0;
  }, []);

  return {
    aiEngine,
    isAiThinking,
    aiThinkingTime,
    aiStats,
    engineType,  // NEW: Expose engine type
    makeAiMove: executeAIMove,
    resetAIStats,
  };
};
